import { LogOut, Settings } from 'lucide-react';
import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  ACCENT_THEMES,
  COLOR_ACCENT_IDS,
  SERIES_ACCENT_IDS,
  useAccentTheme,
} from '../../contexts/AccentThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useMeasurementSystem } from '../../contexts/SpeedUnitContext';
import { cn } from '../../lib/utils';


/** One color choice in the accent / series grids. */
function AccentSwatch({ id, selected, onSelect }) {
  const option = ACCENT_THEMES[id];
  return (
    <button
      type="button"
      role="menuitemradio"
      aria-checked={selected}
      title={option.label}
      aria-label={option.label}
      onClick={() => onSelect(id)}
      className={cn(
        'flex flex-col items-center gap-1 rounded-lg px-1 py-1.5 transition-colors',
        selected ? 'bg-lap-raised' : 'hover:bg-lap-raised/70'
      )}
    >
      <span
        className={cn(
          'w-5 h-5 rounded-full',
          selected && 'ring-2 ring-white ring-offset-1 ring-offset-lap-card'
        )}
        style={{ backgroundColor: `rgb(${option.accent})` }}
      />
      <span className={cn('text-[10px] leading-none', selected ? 'text-lap-text' : 'text-lap-dim')}>
        {option.label}
      </span>
    </button>
  );
}

/**
 * Single chrome control for units, accent, and logout.
 * Lives in the mobile top bar and desktop sidebar so pages stop hosting
 * their own Imperial/Metric + palette clusters.
 */
export function AppSettingsMenu({ className, align = 'right' }) {
  const { measurementSystem, setMeasurementSystem } = useMeasurementSystem();
  const { accent, setAccent } = useAccentTheme();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState(null);
  const rootRef = useRef(null);
  const menuRef = useRef(null);
  const menuId = useId();
  const theme = ACCENT_THEMES[accent] || ACCENT_THEMES.blue;

  useLayoutEffect(() => {
    if (!open || !rootRef.current) {
      setMenuPos(null);
      return undefined;
    }
    const place = () => {
      const r = rootRef.current.getBoundingClientRect();
      const pad = 8;
      const width = 220;
      const left =
        align === 'left'
          ? Math.max(pad, r.left)
          : Math.max(pad, Math.min(r.right - width, window.innerWidth - width - pad));
      const spaceBelow = window.innerHeight - r.bottom;
      const height = 360;
      const top =
        spaceBelow >= height + pad
          ? r.bottom + 6
          : Math.max(pad, r.top - height - 6);
      setMenuPos({ left, top, width });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, align]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (e) => {
      if (rootRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const id = window.setTimeout(() => document.addEventListener('pointerdown', onPointer), 0);
    document.addEventListener('keydown', onKey);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const menu =
    open &&
    menuPos &&
    createPortal(
      <div
        ref={menuRef}
        id={menuId}
        role="menu"
        aria-label="Settings"
        className="fixed z-[120] rounded-xl border border-lap-hairline bg-lap-card p-3 shadow-xl space-y-3"
        style={{ left: menuPos.left, top: menuPos.top, width: menuPos.width }}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <div>
          <div className="text-[10px] uppercase tracking-wider text-lap-dim mb-1.5">Units</div>
          <div className="flex rounded-lg border border-lap-hairline bg-lap-bg p-0.5" role="group" aria-label="Unit system">
            <button
              type="button"
              role="menuitemradio"
              aria-checked={measurementSystem === 'mph'}
              onClick={() => setMeasurementSystem('mph')}
              className={cn(
                'flex-1 px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors',
                measurementSystem === 'mph' ? 'bg-lap-accent text-white' : 'text-lap-muted hover:text-white'
              )}
            >
              Imperial
            </button>
            <button
              type="button"
              role="menuitemradio"
              aria-checked={measurementSystem === 'kmh'}
              onClick={() => setMeasurementSystem('kmh')}
              className={cn(
                'flex-1 px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors',
                measurementSystem === 'kmh' ? 'bg-lap-accent text-white' : 'text-lap-muted hover:text-white'
              )}
            >
              Metric
            </button>
          </div>
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-wider text-lap-dim mb-1.5">Accent</div>
          <div className="grid grid-cols-3 gap-1">
            {COLOR_ACCENT_IDS.map((id) => (
              <AccentSwatch key={id} id={id} selected={accent === id} onSelect={setAccent} />
            ))}
          </div>
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-wider text-lap-dim mb-1.5">Series</div>
          <div className="grid grid-cols-3 gap-1">
            {SERIES_ACCENT_IDS.map((id) => (
              <AccentSwatch key={id} id={id} selected={accent === id} onSelect={setAccent} />
            ))}
          </div>
          <p className="mt-1.5 text-[10px] leading-snug text-lap-dim">
            Applies the series colors and wordmark.
          </p>
        </div>

        <div className="pt-1 border-t border-lap-hairline">
          <button
            type="button"
            role="menuitem"
            onClick={async () => {
              setOpen(false);
              await logout();
              navigate('/Laptura/login');
            }}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-lg bg-lap-raised hover:bg-lap-hairline text-lap-text transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" />
            Logout
          </button>
        </div>
      </div>,
      document.body
    );

  return (
    <div ref={rootRef} className={cn('relative inline-flex', className)}>
      <button
        type="button"
        aria-label="Settings"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        title="Settings"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'inline-flex items-center justify-center h-8 w-8 rounded-full border border-lap-hairline bg-lap-card',
          'text-lap-muted hover:text-lap-text transition-colors'
        )}
      >
        <span className="relative inline-flex">
          <Settings className="w-3.5 h-3.5" />
          <span
            className="absolute -right-0.5 -bottom-0.5 w-2 h-2 rounded-full ring-1 ring-lap-card"
            style={{ backgroundColor: `rgb(${theme.accent})` }}
          />
        </span>
      </button>
      {menu}
    </div>
  );
}
